import React from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { motion } from 'framer-motion';
import { Unlock, Bell, Check } from 'lucide-react';

interface TrialTimelineScreenProps {
  onStartTrial: () => void;
  onClose: () => void;
  onBack: () => void;
}

const timelineItems = [
  {
    icon: <Unlock className="w-6 h-6" />,
    day: 'Dnes',
    description: 'Odemkni plný přístup ke všem funkcím'
  },
  {
    icon: <Bell className="w-6 h-6" />,
    day: 'Den 2',
    description: 'Připomeneme ti, že zkouška brzy skončí'
  },
  {
    icon: <Check className="w-6 h-6" />,
    day: 'Den 3',
    description: 'Platba. Pokud tě aplikace nebude bavit, jedním kliknutím ji zrušíš.'
  }
];


const TrialTimelineScreen: React.FC<TrialTimelineScreenProps> = ({
  onStartTrial,
  onClose,
  onBack
}) => {
  return (
    <div className="w-full max-w-2xl px-6 flex flex-col items-center">

      {/* Heading */}
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
        className="text-4xl sm:text-5xl font-bold text-white text-center mb-6"
      >
        Za{' '}
        <span className="text-brand-orange">2 dny</span>{' '}
        ti dáme vědět
      </motion.h1>

      {/* Main Content with Animation and Timeline */}
      <div className="flex items-center justify-center gap-4 sm:gap-6 w-full">
        {/* Bell Animation - Left Side */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25, duration: 0.5 }}
          className="w-40 h-40 sm:w-48 sm:h-48 flex-shrink-0"
        >
          <DotLottieReact
            src="/materials/icons/Bell Ringing.lottie"
            loop={true}
            autoplay={true}
            className="w-full h-full"
          />
        </motion.div>

        {/* Timeline Box - Shifted Right */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                delay: 0.3,
                duration: 0.5,
                when: "beforeChildren",
                staggerChildren: 0.4 // Faster stagger for better pacing
              }
            }
          }}
          className="hidden sm:block flex-1 bg-white/10 backdrop-blur-md rounded-2xl p-4 sm:p-5"
        >
          {timelineItems.map((item, index) => (
            <motion.div
              key={item.day}
              variants={{
                hidden: { opacity: 0, y: -20 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
              }}
              className="relative"
            >
              <div className="flex items-center gap-3">
                {/* Icon */}
                <div className="w-10 h-10 flex items-center justify-center text-white flex-shrink-0 relative z-10">
                  {item.icon}
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h3 className="text-white font-bold text-sm sm:text-base">{item.day}</h3>
                  <p className="text-white/60 text-xs sm:text-sm">{item.description}</p>
                </div>
              </div>

              {/* Vertical dots between items */}
              {index < timelineItems.length - 1 && (
                <div className="w-10 flex justify-center py-2">
                  <motion.div
                    variants={{
                      hidden: { opacity: 0 },
                      visible: {
                        opacity: 1,
                        transition: {
                          delayChildren: 0.4, // Wait for item text to settle
                          staggerChildren: 0.1 // Stagger dots to create "drawing" effect
                        }
                      }
                    }}
                    className="flex flex-col items-center gap-1.5"
                  >
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        variants={{
                          hidden: { opacity: 0, scale: 0 },
                          visible: { opacity: 1, scale: 1 }
                        }}
                        className="w-1 h-1 rounded-full bg-white/30"
                      />
                    ))}
                  </motion.div>
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* CTA Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="w-full max-w-sm mt-6 mx-auto"
      >
        <button
          onClick={onStartTrial}
          className="w-full bg-white hover:bg-gray-50 text-gray-900 text-lg font-bold py-4 sm:py-5 rounded-2xl transition-all border-b-[6px] border-gray-300 active:border-b-2 active:translate-y-[4px] shadow-lg shadow-white/10 mb-4"
        >
          Zahájit 3-denní zkušební dobu
        </button>
      </motion.div>

    </div>
  );
};

export default TrialTimelineScreen;